import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import type {CookieConsentContextProps} from '../../../contexts/cookie-consent-context'
import useCookieConsent from '../../../hooks/use-cookie-consent'
import ActivityIndicator from '../../atoms/activity-indicator'
import Paragraph from '../../atoms/paragraph'

const CookieConsentBanner = dynamic(
  () => import('../../molecules/cookie-consent-banner'),
  {ssr: false, loading: () => <ActivityIndicator />},
)

const CookieConsentDialog = dynamic(
  () => import('../../molecules/cookie-consent-dialog'),
  {ssr: false, loading: () => <ActivityIndicator />},
)

export function CookieConsent() {
  const [consentConfig, setConsentConfig] = useCookieConsent()
  const {t} = useTranslation('common')

  function handleCancel() {
    setConsentConfig(config => ({...config, dialogOpen: false}))
  }

  function handleSave(config: CookieConsentContextProps) {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('cookie-consent', JSON.stringify(config))
    }

    setConsentConfig(config)
  }

  return (
    <>
      {!consentConfig.finished && (
        <CookieConsentBanner
          title={t('cookie-consent.banner.title')}
          description={t('cookie-consent.banner.description')}
          linkText={t('cookie-consent.banner.read-more')}
          href="/cookie-policy"
          onChangePreferences={() =>
            setConsentConfig(config => ({...config, dialogOpen: true}))
          }
          onDecline={() =>
            handleSave({
              finished: true,
              types: {tracking: false, performance: false},
            })
          }
          onAccept={() =>
            handleSave({
              finished: true,
              types: {tracking: true, performance: true},
            })
          }
        />
      )}

      {!consentConfig.finished && consentConfig.dialogOpen && (
        <CookieConsentDialog
          title={t('cookie-consent.dialog.title')}
          description={
            <Trans
              components={[
                <Paragraph
                  className="mb-2"
                  fontSize="text-base"
                  key="paragraph-1"
                />,
                <Paragraph fontSize="text-base" key="paragraph-2" />,
              ]}
              i18nKey="common:cookie-consent.dialog.description"
            />
          }
          href="/cookie-policy"
          cancelButtonText={t('cookie-consent.dialog.cancel')}
          saveButtonText={t('cookie-consent.dialog.save')}
          linkText={t('cookie-consent.banner.read-more')}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      )}
    </>
  )
}

export default CookieConsent
